'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { getStoreInventory } from '@/lib/thegame/store-client';
import ProductGrid from '@/components/store/product-grid';
import StoreFilters from '@/components/store/store-filters';
import { motion } from 'framer-motion';
import { ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCartItemCount, useCartStore } from '@/lib/store/cart-store';

/**
 * Main Store Content Component
 * Handles search params and inventory fetching
 */
function StoreContent() {
  const setDrawerOpen = useCartStore((s) => s.setDrawerOpen);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '20', 10);
  const category = searchParams.get('category') || undefined;
  const collection = searchParams.get('collection') || undefined;
  const search = searchParams.get('search') || undefined;

  const [products, setProducts] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const cartItemCount = useCartItemCount();

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  useEffect(() => {
    setMounted(true);
    let cancelled = false;

    const loadProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getStoreInventory(false, page, limit, category, collection, search);
        if (cancelled) {
          return;
        }
        const { success, items, totalPages: apiTotalPages, error: apiError } = result;
        if (success && Array.isArray(items)) {
          const forSaleItems = items.filter((item: any) => item.status === 'for-sale');
          setProducts(forSaleItems);
          setTotalPages(apiTotalPages || 1);
          setError(null);
        } else {
          setError(apiError || 'Failed to load products');
        }
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        console.error('[Store Page] Failed to load products:', loadError);
        setError(loadError instanceof Error ? loadError.message : 'Failed to load products');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadProducts();
    return () => {
      cancelled = true;
    };
  }, [page, limit, category, collection, search]);

  return (
    <div className="min-h-screen bg-background text-foreground pt-16 transition-colors duration-300">
      {/* Store Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="py-8"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
            <h1 className="text-lg md:text-lg font-black tracking-tighter text-white uppercase">online-store</h1>
            
            <div className="flex-1 w-full flex justify-center">
              <div className="w-full max-w-3xl">
                <StoreFilters />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-4 py-2 text-white transition-all hover:bg-white/10 hover:scale-105 active:scale-95 ml-auto lg:ml-0"
            >
              <ShoppingBag className="h-4 w-4 text-cyan-400" />
              <span className="text-sm font-bold">Cart</span>
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-cyan-500 px-1.5 text-[10px] font-black text-black shadow-sm">
                {mounted ? cartItemCount : 0}
              </span>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Loading State */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-20"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-64 bg-muted rounded-lg"></div>
                  <div className="mt-4 space-y-3">
                    <div className="h-6 bg-muted rounded"></div>
                    <div className="h-4 bg-muted/60 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-muted-foreground mt-8">Loading products...</p>
          </motion.div>
        )}

        {/* Error State */}
        {!loading && error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-20 text-center"
          >
            <div className="max-w-md mx-auto bg-destructive/10 border border-destructive rounded-xl p-8">
              <h2 className="text-2xl font-bold text-destructive mb-4">Error Loading Products</h2>
              <p className="text-muted-foreground">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-6 px-6 py-3 bg-destructive text-destructive-foreground font-bold rounded-xl hover:bg-destructive/90 transition-colors"
              >
                Try Again
              </button>
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && !error && products.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-20 text-center"
          >
            <h2 className="text-3xl font-bold text-muted-foreground mb-4">No Products Available</h2>
            <p className="text-muted-foreground mb-8">Check back soon for new arrivals!</p>
          </motion.div>
        )}

        {/* Product Grid */}
        {!loading && !error && products.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ProductGrid products={products} />

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-16 flex flex-col items-center gap-6">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-accent hover:text-accent-foreground transition-all"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                      const isCurrent = p === page;
                      const isNear = Math.abs(p - page) <= 2;
                      const isEnd = p === 1 || p === totalPages;

                      if (!isNear && !isEnd) {
                        if (p === 2 || p === totalPages - 1) return <span key={p} className="px-2 text-muted-foreground">...</span>;
                        return null;
                      }

                      return (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`min-w-[40px] h-10 rounded-lg font-bold transition-all ${
                            isCurrent
                              ? 'bg-primary text-primary-foreground scale-110 shadow-lg'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-accent hover:text-accent-foreground transition-all"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground font-medium">
                  Page <span className="text-foreground">{page}</span> of <span className="text-foreground">{totalPages}</span>
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

/**
 * Store Page Wrapper
 * Implements Suspense boundary for search params
 */
export default function StorePage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background pt-32 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Store...</p>
        </div>
      </div>
    }>
      <StoreContent />
    </Suspense>
  );
}